import {Alert, Backdrop, Box, CircularProgress, IconButton} from '@mui/material';
import {GridCloseIcon} from '@mui/x-data-grid';
import {useCallback, useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {HazardEventLog} from '../../../interfaces/HazardAILog';
import ModalFixed from '../../common/ModalFixed';
import HazardVideoOverlayPlayer from './HazardVideoOverlayPlayer';

interface Props {
  open: boolean;
  id: number;
  onClose: () => void;
}

export const HazardDetectionMediaModal = ({open, id, onClose}: Props) => {
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedMedia, setFetchedMedia] = useState<HazardEventLog | null>(null);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);

  const fetchMedia = useCallback(async (mediaId: number) => {
    setIsFetching(true);
    setFetchedErrors([]);
    try {
      const resp = await API.get<HazardEventLog>(
        `${apiBaseUrl}/hazard-ai/detection-event/${mediaId}`
      );
      setFetchedMedia(resp.data ?? null);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_d186e74a');
      setFetchedErrors(messages);
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    if (open && id) {
      fetchMedia(id);
    } else {
      setFetchedMedia(null);
      setFetchedErrors([]);
    }
  }, [open, id]);

  return (
    <ModalFixed open={open} width={window?.innerWidth - 100}>
      <Box
        p={0}
        sx={{
          display: 'flex',
          maxHeight: '90vh',
          height: '100vh',
          img: {
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            objectPosition: 'center',
            objectFit: 'contain',
          },
          video: {
            width: '100%',
            height: '100%',
            objectPosition: 'center',
            objectFit: 'contain',
          },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            zIndex: 1,
            position: 'absolute',
            right: 10,
            top: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <GridCloseIcon />
        </IconButton>
        <Box width="100%" height="100%">
          {fetchedErrors.map((error, index) => (
            <Alert key={index} severity="error" sx={{mb: 2}}>
              {error}
            </Alert>
          ))}
          {!isFetching && fetchedMedia?.video_url && fetchedMedia?.overlay ? (
            <HazardVideoOverlayPlayer
              videoUrl={fetchedMedia.video_url}
              overlay={fetchedMedia.overlay}
            />
          ) : !isFetching && fetchedMedia?.image ? (
            <Box
              sx={{height: '100%', width: '100%'}}
              display="flex"
              dangerouslySetInnerHTML={{__html: fetchedMedia.image}}
            />
          ) : null}
        </Box>
        <Backdrop open={isFetching} sx={{position: 'absolute', zIndex: 1199}}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </ModalFixed>
  );
};

export default HazardDetectionMediaModal;
